import React, { useEffect, useState } from 'react';
import { useApi } from '../../core/hooks/useApi';
import { transactionApi } from '../../core/api/transactionApi';
import { Card } from '../../components/ui/Card';

interface Transaction {
  id: string;
  type: string;
  amount: number;
  currency: string;
  method: string;
  status: string;
  reference: string;
  description?: string;
  createdAt: string;
}

interface ApiResponse {
  success: boolean;
  data?: {
    data?: Transaction[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const { execute: fetchTransactions } = useApi(transactionApi.listTransactions);

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const response = (await fetchTransactions()) as ApiResponse;
      console.log('Transactions response:', response);
      
      // Handle different response structures
      let transactionsData: Transaction[] = [];
      
      // Check for nested data structure
      if (response?.data?.data && Array.isArray(response.data.data)) {
        transactionsData = response.data.data;
      } 
      // Check for direct data array
      else if (response?.data && Array.isArray(response.data)) {
        transactionsData = response.data;
      } 
      // Check if response itself is an array
      else if (Array.isArray(response)) {
        transactionsData = response;
      }
      
      setTransactions(transactionsData);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Transactions</h1>
      
      {transactions.length === 0 ? (
        <Card className="text-center py-12">
          <p className="text-gray-500">No transactions yet.</p>
          <p className="text-sm text-gray-400 mt-2">Your transactions will appear here once you make a transaction.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {transactions.map((transaction) => (
            <Card key={transaction.id} className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{transaction.type}</p>
                  <p className="text-sm text-gray-500">{transaction.reference}</p>
                </div>
                <div className="text-right">
                  <p className={`font-medium ${transaction.type === 'deposit' ? 'text-green-600' : 'text-red-600'}`}>
                    {transaction.type === 'deposit' ? '+' : '-'}${transaction.amount}
                  </p>
                  <span className={`inline-block px-2 py-1 text-xs rounded-full ${getStatusColor(transaction.status)}`}>
                    {transaction.status}
                  </span>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-400">
                {new Date(transaction.createdAt).toLocaleString()}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};